import ConfirmDialog from "@components/ConfirmDialog";
import Label from "@components/Label";
import { yupResolver } from "@hookform/resolvers/yup";
import useLocales from "@hooks/useLocales";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useCommonStyles } from "@pages/ManualDocumentV2/hooks/useCommonStyles";
import { useDocumentSeries } from "@services/manualDocument/useDocumentSeries";
import {
  ComplianceDocumentType,
  DocumentClassType,
} from "@utils/documentSeriesEnum";
import { fDateTime } from "@utils/formatTime";
import { CircleX } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import usePermissions from "src/hooks/usePermissions";
import * as Yup from "yup";
import {
  IItemsEntity,
  ITableHead,
} from "../../../../@types/manualDocument/documentSeriesTypes";
import { DocumentsAddForm } from "./DocumentsAddForm";
import { DocumentSeriesFilter } from "./DocumentSeriesFilter";
import { DocumentsTable } from "./DocumentsTable";

interface IDialogProps {
  isOpen: boolean;
  onClose: () => void;
  documentClass: DocumentClassType;
  complianceType: ComplianceDocumentType;
  onRefreshParent?: () => void;
}

interface IFormValues {
  searchSeries: string;
  series: string;
  initialSequenceNumber: number | string;
}

export const DocumentSeriesDialog: React.FC<IDialogProps> = ({
  isOpen,
  onClose,
  documentClass,
  complianceType,
  onRefreshParent,
}) => {
  const { translate } = useLocales("manual_document_screen");
  const { permissionHandle } = usePermissions();
  const { inputContainer } = useCommonStyles();
  const {
    data,
    isLoading,
    searchDocumentNoSeries,
    createDocumentNoSeries,
    deleteDocumentNoSeries,
  } = useDocumentSeries();

  const [debouncedSearchSeries, setDebouncedSearchSeries] = useState<string>("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deactivateId, setDeactivateId] = useState<string | null>(null);
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);
  const [isAddConfirmOpen, setIsAddConfirmOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<IFormValues | null>(null);
  const isWriteDisabled = !permissionHandle(["ManualDocumentsSeries_Write"]);
  const { palette } = useTheme();

  const TABLE_HEAD: ITableHead[] = [
    { id: "series", label: translate("series"), align: "left" },
    { id: "sequence_num", label: translate("sequence_num"), align: "left" },
    { id: "date", label: translate("date"), align: "left" },
    { id: "actions", label: translate("actions"), align: "right" },
  ];

  const DocumentSeriesSchema = Yup.object().shape({
    series: Yup.string().required(translate("series_is_required")),
    initialSequenceNumber: Yup.number()
      .typeError(translate("invalid_number"))
      .required(translate("number_is_required"))
      .integer(translate("no_decimals"))
      .min(0, translate("positive_integer")),
  });

  const methods = useForm<IFormValues>({
    resolver: yupResolver(DocumentSeriesSchema),
    defaultValues: { searchSeries: "", series: "", initialSequenceNumber: "" },
  });

  const { handleSubmit, reset, watch } = methods;
  const searchSeriesValue = watch("searchSeries");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchSeries(searchSeriesValue || "");
    }, 500);
    return () => clearTimeout(timer);
  }, [searchSeriesValue]);

  useEffect(() => {
    if (isOpen) {
      searchDocumentNoSeries(documentClass, complianceType);
    }
  }, [isOpen, documentClass, complianceType]);

  useEffect(() => {
    if (isOpen) {
      reset();
      setIsAddFormVisible(false);
    }
  }, [isOpen, reset]);

  const executeCreateSequence = async (formData: IFormValues) => {
    if (activeSeriesItem?.id) {
      const isDeleted = await deleteDocumentNoSeries(
        activeSeriesItem.id,
        documentClass,
        complianceType,
      );
      if (!isDeleted) return;
    }

    const isSuccess = await createDocumentNoSeries(
      formData.series,
      Number(formData.initialSequenceNumber),
      documentClass,
      complianceType,
    );
    if (isSuccess) {
      reset({ ...formData, series: "", initialSequenceNumber: "" });
      setIsAddFormVisible(false);
      onRefreshParent?.();
      searchDocumentNoSeries(documentClass, complianceType);
    }
  };

  const onSubmit = async (formData: IFormValues) => {
    if (isWriteDisabled) return;
    if (!formData.series || formData.initialSequenceNumber === "") return;

    if (activeSeriesItem?.id) {
      setPendingFormData(formData);
      setIsAddConfirmOpen(true);
      return;
    }

    await executeCreateSequence(formData);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    await deleteDocumentNoSeries(deleteId, documentClass, complianceType);
    setDeleteId(null);
    onRefreshParent?.();
    searchDocumentNoSeries(documentClass, complianceType);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateId) return;
    const isSuccess = await deleteDocumentNoSeries(
      deactivateId,
      documentClass,
      complianceType,
    );
    if (isSuccess) {
      onRefreshParent?.();
      searchDocumentNoSeries(documentClass, complianceType);
    }
    setDeactivateId(null);
  };

  const filteredData = useMemo(() => {
    return Array.isArray(data)
      ? data
        ?.filter((filteredItems) => !filteredItems.isActive)
        .filter(
          (item: IItemsEntity) =>
            item?.series
              ?.toLowerCase()
              .includes(debouncedSearchSeries.toLowerCase()) ||
            item.initialSequenceNumber
              ?.toString()
              .includes(debouncedSearchSeries.toLowerCase()),
        )
      : [];
  }, [data, debouncedSearchSeries]);

  const activeSeriesItem = useMemo(
    () => (Array.isArray(data) ? data.find((item) => item.isActive) : null),
    [data],
  );

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <ConfirmDialog
            open={!!deleteId}
            onClose={() => setDeleteId(null)}
            title={translate("deactivate_sequence_title")}
            content={translate("deactivate_sequence_note")}
            confirmText="deactivate"
            onConfirm={handleConfirmDelete}
            confirmLoading={isLoading}
            isManualFormComponent={true}
          />
          <ConfirmDialog
            open={!!deactivateId}
            onClose={() => setDeactivateId(null)}
            title={translate("deactivate_sequence_title")}
            content={translate("deactivate_sequence_note")}
            confirmText="deactivate"
            cancelText="cancel"
            onConfirm={handleConfirmDeactivate}
            confirmLoading={isLoading}
            isManualFormComponent={true}
          />
          <ConfirmDialog
            open={isAddConfirmOpen}
            onClose={() => {
              setIsAddConfirmOpen(false);
              setPendingFormData(null);
            }}
            title={translate("add_new")}
            content={translate("add_series_warning")}
            confirmText="continue"
            hideIcon={true}
            onConfirm={() => {
              if (pendingFormData) {
                executeCreateSequence(pendingFormData);
              }
              setIsAddConfirmOpen(false);
              setPendingFormData(null);
            }}
            confirmLoading={isLoading}
            isManualFormComponent={true}
          />
          <Stack direction="column" spacing={0.5}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: palette.text.primary }}
            >
              {translate("customize_invoice_sequencing_number")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate("sequence_number_sub_text")}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ mt: -0.5 }}>
            <CircleX />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "75vh",
            p: 3,
            gap: 2,
          }}
        >
          <FormProvider {...methods}>
            {activeSeriesItem && (
              <Box sx={inputContainer}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {`${activeSeriesItem.series}-${activeSeriesItem.initialSequenceNumber}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {translate("created_on")}{" "}
                      {fDateTime(
                        activeSeriesItem.createdOn ||
                        activeSeriesItem.updatedOn ||
                        "",
                      )}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Label color="success">{translate("Active")}</Label>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={isLoading || isWriteDisabled}
                      onClick={() => setDeactivateId(activeSeriesItem?.id!)}
                    >
                      {translate("deactivate")}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={isLoading || isWriteDisabled || isAddFormVisible}
                      onClick={() => setIsAddFormVisible(true)}
                      sx={{
                        backgroundColor: "grey.700",
                        color: "common.white",
                        "&:hover": {
                          backgroundColor: "grey.800",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "grey.400",
                          color: "common.white",
                        },
                      }}
                    >
                      {translate("add_new")}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            )}

            {(!activeSeriesItem || isAddFormVisible) && (
              <DocumentsAddForm
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                translate={translate}
                isLoading={isLoading}
                isWriteDisabled={isWriteDisabled}
              />
            )}

            <Divider />
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle1">
                {translate("deactive_series")}
              </Typography>
              <DocumentSeriesFilter translate={translate} />
              <DocumentsTable
                isLoading={isLoading}
                documentSeriesData={filteredData}
                isWriteDisabled={isWriteDisabled}
                setDeleteId={(id) => setDeleteId(id)}
                TABLE_HEAD={TABLE_HEAD}
                translate={translate}
              />
            </Stack>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </>
  );
};
